import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Switch } from "@/components/ui/switch";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { ActivitiesSummaryCards } from "@/components/activities/ActivitiesSummaryCards";
import { ResearchActivityForm } from "@/components/activities/ResearchActivityForm";
import { UnmatchedEmailMatcher } from "@/components/activities/UnmatchedEmailMatcher";

import { 
  Phone, 
  Mail, 
  Calendar as CalendarIcon, 
  FileText, 
  Send, 
  User, 
  Plus, 
  Search, 
  CalendarDays,
  AlertCircle,
  Loader2,
  Clock,
  Users,
  Building,
  Bell,
  Trash2,
  Edit,
  Linkedin,
  RefreshCw
} from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/components/AuthProvider";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { SimpleMultiSelect } from "@/components/ui/simple-multi-select";
import { Activity } from "@/services/common/types";
import { activitiesService } from "@/services/activitiesService";
import { useProposals } from "@/features/proposals";

// Generate year options (2023 to current year + 3)
const generateYearOptions = () => {
  const currentYear = new Date().getFullYear();
  const startYear = 2023;
  const endYear = currentYear + 3;
  const years: string[] = [];
  
  for (let year = startYear; year <= endYear; year++) {
    years.push(year.toString());
  }
  
  return years;
};

// Activity types configuration
const activityTypes = [
  { 
    value: "email", 
    label: "Email", 
    icon: Mail, 
    color: "bg-blue-500 hover:bg-blue-600 text-white",
    iconColor: "text-blue-600",
    bgColor: "bg-blue-50"
  },
  { 
    value: "call", 
    label: "Call", 
    icon: Phone, 
    color: "bg-green-500 hover:bg-green-600 text-white",
    iconColor: "text-green-600",
    bgColor: "bg-green-50"
  },
  { 
    value: "meeting", 
    label: "Meet", 
    icon: CalendarIcon, 
    color: "bg-purple-500 hover:bg-purple-600 text-white",
    iconColor: "text-purple-600",
    bgColor: "bg-purple-50"
  },
  { 
    value: "linkedin", 
    label: "LinkedIn", 
    icon: Linkedin, 
    color: "bg-sky-600 hover:bg-sky-700 text-white",
    iconColor: "text-sky-600",
    bgColor: "bg-sky-50"
  }
];

// Response status configuration
const responseStatusOptions = [
  { value: "no_response", label: "No response", color: "bg-gray-100 text-gray-700" },
  { value: "not_interested", label: "Not interested", color: "bg-red-100 text-red-700" },
  { value: "interested", label: "Interested", color: "bg-yellow-100 text-yellow-700" },
  { value: "proposal", label: "Proposal", color: "bg-green-100 text-green-700" }
];

// Form validation schema
const activitySchema = z.object({
  type: z.string().min(1, "Activity type is required"),
  client_id: z.string().optional(),
  person_ids: z.array(z.string()).optional(),
  year: z.string().optional(),
  proposal_id: z.string().optional(),
  initiative_id: z.string().optional(),
  subject: z.string().optional(),
  description: z.string().optional(),
  activity_date: z.date(),
  assigned_to: z.string().optional(),
  duration_minutes: z.number().optional(),
  response_status: z.string().optional(),
});

// Extended Activity type with joined relations
type ActivityWithJoins = Activity & {
  clients?: { name: string; id: string };
  initiatives?: { name: string; id: string };
  projects?: { name: string; id: string };
  proposals?: { title: string; id: string };
  creator?: { id: string; first_name: string; last_name: string; full_name: string; profile_image_url?: string };
  email_messages?: { from_addr: string; to_addrs: string[]; cc_addrs?: string[]; message_id: string };
  people?: { id: string; first_name: string; last_name: string; email: string }[]; // Changed to array
};

// Time-based activity filters
function getRelativeTime(activityDate: string) {
  const now = new Date();
  const date = new Date(activityDate);
  const diffInMs = date.getTime() - now.getTime();
  const isFuture = diffInMs > 0;
  const diffInHours = Math.abs(diffInMs) / (1000 * 60 * 60);
  
  if (diffInHours < 1) {
    return isFuture ? "Soon" : "Just now";
  } else if (diffInHours < 24) {
    const hours = Math.floor(diffInHours);
    return isFuture ? `in ${hours} hours` : `${hours} hours ago`;
  } else {
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays === 1) return isFuture ? "Tomorrow" : "Yesterday";
    if (diffInDays < 7) return isFuture ? `in ${diffInDays} days` : `${diffInDays} days ago`;
    if (diffInDays < 30) {
      const weeks = Math.floor(diffInDays / 7);
      return isFuture ? `in ${weeks} weeks` : `${weeks} weeks ago`;
    }
    const months = Math.floor(diffInDays / 30);
    return isFuture ? `in ${months} months` : `${months} months ago`;
  }
}

export default function Activities() {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedUser, setSelectedUser] = useState<string>("my_activities");
  const [includeDirectReports, setIncludeDirectReports] = useState<boolean>(false);
  const [selectedTimeRange, setSelectedTimeRange] = useState<string>("All");
  const [selectedProposal, setSelectedProposal] = useState<string>("all");
  const [selectedInitiative, setSelectedInitiative] = useState<string>("all");
  const [initiativeSearchTerm, setInitiativeSearchTerm] = useState("");
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { toast } = useToast();

  // Get user profile
  const { data: userProfile } = useQuery({
    queryKey: ["user-profile", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const { data, error } = await supabase
        .from("profiles")
        .select("id, user_id, first_name, last_name, full_name")
        .eq("user_id", user.id)
        .single();
      
      if (error) throw error;
      return data;
    },
    enabled: !!user?.id,
  });

  // Only show dropdown if there are direct reports
  // For now, only current user is available (no direct reports)
  const availableAssignees = [
    { id: userProfile?.id, full_name: "Me", first_name: "Me", last_name: "" }
  ].filter(user => user.id);

  // Check if we have more than just the current user
  const hasDirectReports = availableAssignees.length > 1;

  // Dialog states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isResearchModalOpen, setIsResearchModalOpen] = useState(false);
  
  const [editingActivity, setEditingActivity] = useState<ActivityWithJoins | null>(null);
  const [isEmailSyncing, setIsEmailSyncing] = useState(false);

  // Form setup
  const form = useForm<z.infer<typeof activitySchema>>({
    resolver: zodResolver(activitySchema),
    defaultValues: {
      type: "",
      client_id: "",
      person_ids: [],
      year: new Date().getFullYear().toString(),
      proposal_id: "",
      initiative_id: "",
      subject: "",
      description: "",
      activity_date: new Date(),
      assigned_to: userProfile?.id || "",
      duration_minutes: undefined,
      response_status: "",
    },
  });

  // Edit form setup
  const editForm = useForm<z.infer<typeof activitySchema>>({
    resolver: zodResolver(activitySchema),
    defaultValues: {
      type: "",
      client_id: "",
      person_ids: [],
      year: new Date().getFullYear().toString(),
      proposal_id: "",
      initiative_id: "",
      subject: "",
      description: "",
      activity_date: new Date(),
      assigned_to: userProfile?.id || "",
      duration_minutes: undefined,
      response_status: "",
    },
  });

  // Set edit form values when editing activity is selected
  useEffect(() => {
    if (editingActivity) {
      // Extract person IDs from both junction table and legacy field
      const personIds: string[] = [];
      
      // From junction table
      if ((editingActivity as any).activity_people?.length > 0) {
        (editingActivity as any).activity_people.forEach((ap: any) => {
          if (ap.person_id) personIds.push(ap.person_id);
        });
      }
      
      // From legacy person_id field for backward compatibility
      if ((editingActivity as any).person_id && !personIds.includes((editingActivity as any).person_id)) {
        personIds.push((editingActivity as any).person_id);
      }
      
      editForm.reset({
        type: editingActivity.type,
        client_id: editingActivity.clients?.id || "",
        person_ids: personIds,
        year: editingActivity.activity_date ? new Date(editingActivity.activity_date).getFullYear().toString() : new Date().getFullYear().toString(),
        proposal_id: editingActivity.proposals?.id || "",
        initiative_id: editingActivity.initiatives?.id || "",
        subject: editingActivity.subject || "",
        description: editingActivity.description || "",
        activity_date: editingActivity.activity_date ? new Date(editingActivity.activity_date) : new Date(),
        assigned_to: editingActivity.assigned_to || userProfile?.id || "",
        duration_minutes: editingActivity.duration_minutes || undefined,
        response_status: editingActivity.response_status || "",
      });
    }
  }, [editingActivity, editForm, userProfile?.id]);


  // Fetch activities using the updated service
  const { data: activities = [], isLoading: activitiesLoading, error: activitiesError, refetch: refetchActivities } = useQuery({
    queryKey: ["activities"],
    queryFn: () => activitiesService.getActivities()
  }) as { data: any[], isLoading: boolean, error: any, refetch: any };

  // Real-time subscriptions for activities
  useEffect(() => {
    const channel = supabase
      .channel('activities-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'activities'
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ["activities"] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [queryClient]);

  // Fetch clients for form dropdown
  const { data: clients } = useQuery({
    queryKey: ["clients"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("clients")
        .select("id, name")
        .order("name");
      
      if (error) throw error;
      return data;
    }
  });

  // Fetch people for form dropdown
  const { data: people } = useQuery({
    queryKey: ["people"],
    queryFn: async () => {
      const { data, error } = await supabase
        .rpc("get_people_secure", {});
      
      if (error) throw error;
      return data;
    }
  });

  // Fetch initiatives for form dropdown
  const { data: initiatives, isLoading: initiativesLoading, error: initiativesError } = useQuery({
    queryKey: ["initiatives"],
    queryFn: async () => {
      if (!user?.id) {
        throw new Error("User not authenticated");
      }
      
      const { data, error } = await supabase
        .from("initiatives")
        .select("id, name")
        .order("name");
      
      if (error) {
        console.error("Error fetching initiatives:", error);
        throw error;
      }
      return data || [];
    },
    enabled: !!user?.id
  });

  // Debug initiatives data
  useEffect(() => {
    console.log("Initiatives state:", { 
      initiatives, 
      initiativesLoading, 
      initiativesError,
      uniqueInitiatives: initiatives?.filter((initiative, index, array) => 
        array.findIndex(i => i.id === initiative.id) === index
      ) || []
    });
  }, [initiatives, initiativesLoading, initiativesError]);

  // Fetch users for filter dropdown
  const { data: users } = useQuery({
    queryKey: ["users"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("id, first_name, last_name, full_name, profile_image_url")
        .order("first_name");
      
      if (error) throw error;
      return data;
    }
  });

  // Fetch projects for research activity form
  const { data: projects } = useQuery({
    queryKey: ["projects"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("projects")
        .select(`
          id, 
          name,
          clients!inner(name)
        `)
        .order("name");
      
      if (error) throw error;
      return data;
    }
  });

  // Fetch proposals for form dropdown
  const { data: proposals } = useProposals();

  // Get unique values for dropdowns
  const uniqueClients = clients?.filter((client, index, array) => 
    array.findIndex(c => c.id === client.id) === index
  ) || [];

  const uniquePeople = people?.filter((person, index, array) => 
    array.findIndex(p => p.id === person.id) === index
  ) || [];

  const uniqueInitiatives = initiatives?.filter((initiative, index, array) => 
    array.findIndex(i => i.id === initiative.id) === index
  ) || [];

  // Filter initiatives for search
  const filteredInitiatives = uniqueInitiatives.filter(initiative =>
    initiative.name.toLowerCase().includes(initiativeSearchTerm.toLowerCase())
  );

  // Email sync function
  const syncEmailsToActivities = async () => {
    setIsEmailSyncing(true);
    try {
      const { data, error } = await supabase.functions.invoke('sync-email-activities');
      
      if (error) throw error;
      
      console.log('Email sync result:', data);
      
      // Force refresh activities after sync - wait a bit for database consistency
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ["activities"] });
        refetchActivities();
      }, 1000);
      
      toast({
        title: "Email Sync Complete", 
        description: data.message || `Synced ${data.successfulEmails || 0} emails to activities`,
      });
      
    } catch (error: any) {
      console.error('Email sync error:', error);
      toast({
        title: "Email Sync Failed",
        description: error.message || "Failed to sync emails to activities",
        variant: "destructive",
      });
    } finally {
      setIsEmailSyncing(false);
    }
  };

  // Create activity mutation
  const createActivityMutation = useMutation({
    mutationFn: async (data: z.infer<typeof activitySchema>) => {
      console.log('Creating activity with userProfile:', userProfile);
      console.log('User ID:', user?.id);
      
      // Create activities for each selected person or one general activity
      const peopleIds = data.person_ids && data.person_ids.length > 0 ? data.person_ids : [null];
      
      // Determine if the activity is in the future
      const now = new Date();
      now.setHours(0, 0, 0, 0); // Start of today
      const activityDate = new Date(data.activity_date);
      activityDate.setHours(0, 0, 0, 0);
      const isFuture = activityDate > now;
      
      const activitiesToInsert = peopleIds.map(personId => ({
        type: data.type,
        client_id: data.client_id || null,
        person_id: personId,
        proposal_id: data.proposal_id === "none" || !data.proposal_id ? null : data.proposal_id,
        initiative_id: data.initiative_id === "none" || !data.initiative_id ? null : data.initiative_id,
        subject: data.subject || `${data.type} Activity`,
        description: data.description,
        activity_date: data.activity_date.toISOString(),
        duration_minutes: data.duration_minutes || null,
        created_by: userProfile?.id || null,
        assigned_to: data.assigned_to || userProfile?.id,
        status: isFuture ? "Pending" : "Done",
      }));

      console.log('About to insert activities:', activitiesToInsert);

      const { error } = await supabase
        .from("activities")
        .insert(activitiesToInsert);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["activities"] });
      toast({
        title: "Success",
        description: "Activity logged successfully",
      });
      form.reset({
        type: "",
        client_id: "",
        person_ids: [],
        year: new Date().getFullYear().toString(),
        proposal_id: "",
        initiative_id: "",
        subject: "",
        description: "",
        activity_date: new Date(),
        assigned_to: userProfile?.id || "",
        duration_minutes: undefined,
        response_status: "",
      });
      setIsModalOpen(false);
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to log activity",
        variant: "destructive",
      });
    },
  });

  // Update activity mutation
  const updateActivityMutation = useMutation({
    mutationFn: async (data: z.infer<typeof activitySchema> & { id: string }) => {
      console.log('🔄 Updating activity with data:', data);
      const { id, ...updateData } = data;
      
      // Determine if the activity is in the future
      const now = new Date();
      now.setHours(0, 0, 0, 0); // Start of today
      const activityDate = new Date(updateData.activity_date);
      activityDate.setHours(0, 0, 0, 0);
      const isFuture = activityDate > now;
      
      // Use the proper service method instead of direct Supabase call
      return activitiesService.updateActivity({
        id,
        ...updateData,
        activity_date: updateData.activity_date.toISOString(),
        proposal_id: updateData.proposal_id === "none" || !updateData.proposal_id ? undefined : updateData.proposal_id,
        initiative_id: updateData.initiative_id === "none" || !updateData.initiative_id ? undefined : updateData.initiative_id,
        duration_minutes: updateData.duration_minutes || undefined,
        status: isFuture ? "Pending" : "Done",
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["activities"] });
      toast({
        title: "Success",
        description: "Activity updated successfully",
      });
      setIsEditModalOpen(false);
      setEditingActivity(null);
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update activity",
        variant: "destructive",
      });
    },
  });

  // Delete activity mutation
  const deleteActivityMutation = useMutation({
    mutationFn: async (activityId: string) => {
      console.log('Attempting to delete activity:', activityId);
      
      // First check the user's auth state and org
      const { data: { user } } = await supabase.auth.getUser();
      console.log('Current user:', user?.id);
      
      if (!user) {
        throw new Error('User not authenticated');
      }
      
      // Check user's profile and org_id
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('org_id, role')
        .eq('user_id', user.id)
        .single();
        
      if (profileError) {
        console.error('Profile error:', profileError);
        throw new Error('Failed to get user profile');
      }
      
      console.log('User profile:', profile);
      
      const { error } = await supabase
        .from("activities")
        .delete()
        .eq('id', activityId);

      if (error) {
        console.error('Delete error:', error);
        throw error;
      }
      
      console.log('Activity deleted successfully');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["activities"] });
      toast({
        title: "Success",
        description: "Activity deleted successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to delete activity",
        variant: "destructive",
      });
    },
  });

  // Update activity status mutation
  const updateActivityStatusMutation = useMutation({
    mutationFn: async ({ activityId, status }: { activityId: string; status: string }) => {
      const { error } = await supabase
        .from("activities")
        .update({ status })
        .eq('id', activityId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["activities"] });
      toast({
        title: "Success",
        description: "Activity status updated",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update status",
        variant: "destructive",
      });
    },
  });

  // Update response status mutation
  const updateResponseStatusMutation = useMutation({
    mutationFn: async ({ activityId, responseStatus }: { activityId: string; responseStatus: string }) => {
      const { error } = await supabase
        .from("activities")
        .update({ response_status: responseStatus })
        .eq('id', activityId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["activities"] });
      toast({
        title: "Success",
        description: "Response status updated",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update response status",
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: z.infer<typeof activitySchema>) => {
    createActivityMutation.mutate(data);
  };

  // DEBUG: Log activities data
  console.log('Activities Debug:', {
    totalActivities: activities.length,
    selectedUser,
    userProfileId: userProfile?.id,
    selectedInitiative,
    selectedTimeRange,
    searchTerm,
    sampleActivities: activities.slice(0, 3).map(a => ({
      id: a.id,
      subject: a.subject,
      assignee: a.assignee,
      creator: a.creator,
      activity_date: a.activity_date,
      initiatives: a.initiatives
    }))
  });

  console.log('DEBUG: Activities loading state:', {
    activitiesLoading,
    activitiesError,
    activitiesCount: activities?.length || 0
  });

  // Filter activities based on search and filters
  const filteredActivities = activities.filter(activity => {
    const activityType = activityTypes.find(type => type.value === activity.type);
    const activityTypeLabel = activityType?.label || activity.type;
    
    const matchesSearch = 
      activity.subject?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      activity.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      activity.clients?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      activityTypeLabel?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (activity.people && activity.people.length > 0 && activity.people.map(p => `${p.first_name} ${p.last_name}`).join(', '))?.toLowerCase().includes(searchTerm.toLowerCase());

    // Updated user filtering logic
    let matchesUser = true;
    if (selectedUser === "my_activities") {
      // Check both assignee and creator for backward compatibility
      matchesUser = (activity.assignee?.id === userProfile?.id) || 
                   (!activity.assignee && activity.creator?.id === userProfile?.id);
    } else if (selectedUser === "all") {
      matchesUser = true;
    } else {
      matchesUser = (activity.assignee?.id === selectedUser) || 
                   (!activity.assignee && activity.creator?.id === selectedUser);
    }

    // Proposal filter
    const matchesProposal = selectedProposal === "all" ||
      (selectedProposal === "none" && !activity.proposal_id) ||
      activity.proposal_id === selectedProposal;

    // Initiative filter
    const matchesInitiative = selectedInitiative === "all" ||
      (selectedInitiative === "none" && !activity.initiatives?.id) ||
      activity.initiatives?.id === selectedInitiative;

    // Time range filtering
    const activityDate = new Date(activity.activity_date);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const thisWeekStart = new Date(today.getTime() - (today.getDay() * 24 * 60 * 60 * 1000));
    const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);

    let matchesTimeRange = true;
    if (selectedTimeRange === "Today") {
      matchesTimeRange = activityDate >= today;
    } else if (selectedTimeRange === "This week") {
      matchesTimeRange = activityDate >= thisWeekStart;
    } else if (selectedTimeRange === "This month") {
      matchesTimeRange = activityDate >= thisMonthStart;
    }

    return matchesSearch && matchesUser && matchesProposal && matchesInitiative && matchesTimeRange;
  });

  // Group activities by initiative + client combination
  const groupedActivities = filteredActivities.reduce((groups, activity) => {
    const key = `${activity.initiatives?.id || 'no-initiative'}-${activity.clients?.id || 'no-client'}`;
    if (!groups[key]) {
      groups[key] = {
        initiative: activity.initiatives,
        client: activity.clients,
        activities: []
      };
    }
    groups[key].activities.push(activity);
    return groups;
  }, {} as Record<string, {
    initiative?: { name: string; id: string };
    client?: { name: string; id: string };
    activities: any[];
  }>);

  // DEBUG: Filtered Activities Count
  console.log('DEBUG: Filtered Activities Count:', {
    filteredActivitiesCount: filteredActivities.length,
    totalActivities: activities.length,
    groupsCount: Object.keys(groupedActivities).length,
    sampleFiltered: filteredActivities.slice(0, 3).map(a => ({
      id: a.id,
      type: a.type,
      subject: a.subject,
      assignee: a.assignee?.full_name,
      creator: a.creator?.full_name
    }))
  });

  // Calculate activity counts for summary using filtered activities
  const todayActivities = filteredActivities.filter(activity => {
    const today = new Date();
    const activityDate = new Date(activity.activity_date);
    return activityDate.toDateString() === today.toDateString();
  });

  const todayDonePercentage = todayActivities.length > 0 
    ? Math.round((todayActivities.filter(a => a.status === 'Done').length / todayActivities.length) * 100)
    : 0;

  const thisWeekActivities = filteredActivities.filter(activity => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const thisWeekStart = new Date(today.getTime() - (today.getDay() * 24 * 60 * 60 * 1000));
    const activityDate = new Date(activity.activity_date);
    return activityDate >= thisWeekStart;
  });

  const lastWeekStart = new Date();
  lastWeekStart.setDate(lastWeekStart.getDate() - lastWeekStart.getDay() - 7);
  const lastWeekEnd = new Date(lastWeekStart);
  lastWeekEnd.setDate(lastWeekEnd.getDate() + 6);

  const lastWeekActivities = filteredActivities.filter(activity => {
    const activityDate = new Date(activity.activity_date);
    return activityDate >= lastWeekStart && activityDate <= lastWeekEnd;
  });

  // Time spent calculations using filtered activities
  const timeSpentToday = todayActivities.reduce((total, activity) => {
    return total + (activity.duration_minutes || 0);
  }, 0);

  const timeSpentThisWeek = thisWeekActivities.reduce((total, activity) => {
    return total + (activity.duration_minutes || 0);
  }, 0);

  // Previous day time spent
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  const yesterdayStart = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate());
  const yesterdayEnd = new Date(yesterdayStart);
  yesterdayEnd.setDate(yesterdayEnd.getDate() + 1);

  const yesterdayActivities = filteredActivities.filter(activity => {
    const activityDate = new Date(activity.activity_date);
    return activityDate >= yesterdayStart && activityDate < yesterdayEnd;
  });

  const timeSpentYesterday = yesterdayActivities.reduce((total, activity) => {
    return total + (activity.duration_minutes || 0);
  }, 0);

  // Previous week time spent
  const previousWeekStart = new Date(lastWeekStart);
  const previousWeekEnd = new Date(lastWeekEnd);

  const previousWeekActivities = filteredActivities.filter(activity => {
    const activityDate = new Date(activity.activity_date);
    return activityDate >= previousWeekStart && activityDate <= previousWeekEnd;
  });

  const timeSpentLastWeek = previousWeekActivities.reduce((total, activity) => {
    return total + (activity.duration_minutes || 0);
  }, 0);

  if (activitiesLoading) {
    return (
      <div className="p-6 space-y-4">
        <div className="flex justify-between items-center">
          <Skeleton className="h-8 w-32" />
          <Skeleton className="h-10 w-32" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i}>
              <CardContent className="p-4">
                <Skeleton className="h-16 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
        <div className="space-y-4">
          {[...Array(5)].map((_, i) => (
            <Card key={i}>
              <CardContent className="p-6">
                <Skeleton className="h-20 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (activitiesError) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Alert variant="destructive" className="max-w-md">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Failed to load activities: {activitiesError.message}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Activities</h1>
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={syncEmailsToActivities}
            disabled={isEmailSyncing}
            className="border-green-200 text-green-700 hover:bg-green-50"
          >
            {isEmailSyncing ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            {isEmailSyncing ? 'Syncing...' : 'Sync Emails'}
          </Button>
          <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
            <DialogTrigger asChild>
              <Button className="bg-primary hover:bg-primary/90">
                <Plus className="h-4 w-4 mr-2" />
                New Sales Activity
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>New Sales Activity</DialogTitle>
              </DialogHeader>
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                  <FormField
                    control={form.control}
                    name="type"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Activity Type</FormLabel>
                        <div className="flex gap-3">
                          {activityTypes.map(type => (
                            <Button
                              key={type.value}
                              type="button"
                              variant={field.value === type.value ? "default" : "outline"}
                              className={cn(
                                "flex-1 flex items-center justify-center gap-2",
                                field.value === type.value && type.color
                              )}
                              onClick={() => field.onChange(type.value)}
                            >
                              <type.icon className="h-4 w-4" />
                              {type.label}
                            </Button>
                          ))}
                        </div>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="grid grid-cols-3 gap-4">
                    <FormField
                      control={form.control}
                      name="client_id"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Client Filter (Optional)</FormLabel>
                          <Select onValueChange={field.onChange} defaultValue={field.value}>
                            <FormControl>
                              <SelectTrigger className="bg-background">
                                <SelectValue placeholder="Filter by client" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent className="bg-background z-50">
                              <SelectItem value="all">All clients</SelectItem>
                              {uniqueClients.map((client) => (
                                <SelectItem key={client.id} value={client.id}>
                                  {client.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="person_ids"
                      render={({ field }) => {
                        const selectedClientId = form.watch("client_id");
                        
                        return (
                          <FormItem>
                            <FormLabel>People (Optional)</FormLabel>
                            <FormControl>
                              <SimpleMultiSelect
                                people={uniquePeople || []}
                                selectedPeopleIds={field.value || []}
                                onSelectionChange={field.onChange}
                                placeholder="Select people"
                                clientFilter={selectedClientId === "all" ? undefined : selectedClientId || undefined}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        );
                      }}
                    />

                    <FormField
                      control={form.control}
                      name="year"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Year</FormLabel>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl>
                              <SelectTrigger className="bg-background">
                                <SelectValue placeholder="Select year" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent className="bg-background z-50">
                              {generateYearOptions().map((year) => (
                                <SelectItem key={year} value={year}>
                                  {year}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="proposal_id"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Proposal (Optional)</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger className="bg-background">
                              <SelectValue placeholder="Select proposal" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent className="bg-background z-50">
                            <SelectItem value="none">No proposal</SelectItem>
                            {proposals?.map((proposal) => (
                              <SelectItem key={proposal.id} value={proposal.id}>
                                {proposal.title} - {proposal.clients?.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="initiative_id"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Initiative (Optional)</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger className="bg-background">
                              <SelectValue placeholder="Select initiative" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent className="bg-background z-50">
                            <SelectItem value="none">No specific initiative</SelectItem>
                            {uniqueInitiatives.map((initiative) => (
                              <SelectItem key={initiative.id} value={initiative.id}>
                                {initiative.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="subject"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Topic (Optional)</FormLabel>
                        <FormControl>
                          <Input placeholder="Brief description of the activity" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="grid grid-cols-3 gap-4">
                    {hasDirectReports ? (
                      <FormField
                        control={form.control}
                        name="assigned_to"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Assigned to</FormLabel>
                            <Select onValueChange={field.onChange} value={field.value}>
                              <FormControl>
                                <SelectTrigger className="bg-background">
                                  <SelectValue placeholder="Select assignee" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent className="bg-background z-50">
                                {availableAssignees.map((assignee) => (
                                  <SelectItem key={assignee.id} value={assignee.id}>
                                    {assignee.full_name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    ) : (
                      <div className="space-y-2">
                        <Label>Assigned to</Label>
                        <div className="px-3 py-2 bg-muted rounded-md text-sm">Me</div>
                      </div>
                    )}

                    <FormField
                      control={form.control}
                      name="activity_date"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Activity Date</FormLabel>
                          <Popover>
                            <PopoverTrigger asChild>
                              <FormControl>
                                <Button variant="outline" className={cn(
                                  "w-full pl-3 text-left font-normal",
                                  !field.value && "text-muted-foreground"
                                )}>
                                  {field.value ? (
                                    format(field.value, "PPP")
                                  ) : (
                                    <span>Pick a date</span>
                                  )}
                                  <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                                </Button>
                              </FormControl>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="start">
                              <Calendar
                                mode="single"
                                selected={field.value}
                                onSelect={field.onChange}
                                disabled={(date) => date < new Date("1900-01-01")}
                                initialFocus
                              />
                            </PopoverContent>
                          </Popover>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="duration_minutes"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Duration (Minutes)</FormLabel>
                          <FormControl>
                            <Input
                              type="number"
                              min="0"
                              placeholder="0"
                              {...field}
                              onChange={(e) => {
                                const value = e.target.value === "" ? undefined : parseInt(e.target.value);
                                field.onChange(value);
                              }}
                              value={field.value ?? ""}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="description"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Details (Optional)</FormLabel>
                        <FormControl>
                          <Textarea placeholder="Detailed notes about the activity" rows={3} {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="response_status"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Response Status (Optional)</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger className="bg-background">
                              <SelectValue placeholder="Select response status" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent className="bg-background z-50">
                            {responseStatusOptions.map((status) => (
                              <SelectItem key={status.value} value={status.value}>
                                {status.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <Button type="submit" className="w-full" disabled={createActivityMutation.isPending}>
                    {createActivityMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                    Log Activity
                  </Button>
                </form>
              </Form>
            </DialogContent>
          </Dialog>

          <Button 
            onClick={() => setIsResearchModalOpen(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Research Activity
          </Button>
        </div>
      </div>

      {/* Edit Activity Dialog */}
      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Activity</DialogTitle>
          </DialogHeader>
          <Form {...editForm}>
            <form onSubmit={editForm.handleSubmit((values) => {
              if (editingActivity) {
                updateActivityMutation.mutate({ ...values, id: editingActivity.id });
              }
            })} className="space-y-4">
              <FormField
                control={editForm.control}
                name="type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Activity Type</FormLabel>
                    <div className="flex gap-3">
                      {activityTypes.map(type => (
                        <Button
                          key={type.value}
                          type="button"
                          variant={field.value === type.value ? "default" : "outline"}
                          className={cn(
                            "flex-1 flex items-center justify-center gap-2",
                            field.value === type.value && type.color
                          )}
                          onClick={() => field.onChange(type.value)}
                        >
                          <type.icon className="h-4 w-4" />
                          {type.label}
                        </Button>
                      ))}
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-3 gap-4">
                <FormField
                  control={editForm.control}
                  name="client_id"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Client Filter (Optional)</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger className="bg-background">
                            <SelectValue placeholder="Filter by client" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent className="bg-background z-50">
                          <SelectItem value="all">All clients</SelectItem>
                          {uniqueClients.map((client) => (
                            <SelectItem key={client.id} value={client.id}>
                              {client.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={editForm.control}
                  name="person_ids"
                  render={({ field }) => {
                    const selectedClientId = editForm.watch("client_id");
                    
                    return (
                      <FormItem>
                        <FormLabel>People (Optional)</FormLabel>
                        <FormControl>
                          <SimpleMultiSelect
                            people={uniquePeople || []}
                            selectedPeopleIds={field.value || []}
                            onSelectionChange={field.onChange}
                            placeholder="Select people"
                            clientFilter={selectedClientId === "all" ? undefined : selectedClientId || undefined}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    );
                  }}
                />

                <FormField
                  control={editForm.control}
                  name="year"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Year</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger className="bg-background">
                            <SelectValue placeholder="Select year" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent className="bg-background z-50">
                          {generateYearOptions().map((year) => (
                            <SelectItem key={year} value={year}>
                              {year}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={editForm.control}
                name="proposal_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Proposal (Optional)</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger className="bg-background">
                          <SelectValue placeholder="Select proposal" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent className="bg-background z-50">
                        <SelectItem value="none">No proposal</SelectItem>
                        {proposals?.map((proposal) => (
                          <SelectItem key={proposal.id} value={proposal.id}>
                            {proposal.title} - {proposal.clients?.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={editForm.control}
                name="initiative_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Initiative (Optional)</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger className="bg-background">
                          <SelectValue placeholder="Select initiative" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent className="bg-background z-50">
                        <SelectItem value="none">No specific initiative</SelectItem>
                        {uniqueInitiatives.map((initiative) => (
                          <SelectItem key={initiative.id} value={initiative.id}>
                            {initiative.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={editForm.control}
                name="subject"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Topic (Optional)</FormLabel>
                    <FormControl>
                      <Input placeholder="Brief description of the activity" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-3 gap-4">
                {hasDirectReports ? (
                  <FormField
                    control={editForm.control}
                    name="assigned_to"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Assigned to</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value || userProfile?.id}>
                          <FormControl>
                            <SelectTrigger className="bg-background">
                              <SelectValue placeholder="Select assignee" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent className="bg-background z-50">
                            {availableAssignees.map((assignee) => (
                              <SelectItem key={assignee.id} value={assignee.id}>
                                {assignee.full_name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                ) : (
                  <div className="space-y-2">
                    <Label>Assigned to</Label>
                    <div className="px-3 py-2 bg-muted rounded-md text-sm">Me</div>
                  </div>
                )}

                <FormField
                  control={editForm.control}
                  name="activity_date"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Activity Date</FormLabel>
                      <Popover>
                        <PopoverTrigger asChild>
                          <FormControl>
                            <Button variant="outline" className={cn(
                              "w-full pl-3 text-left font-normal",
                              !field.value && "text-muted-foreground"
                            )}>
                              {field.value ? (
                                format(field.value, "PPP")
                              ) : (
                                <span>Pick a date</span>
                              )}
                              <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                            </Button>
                          </FormControl>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={field.value}
                            onSelect={field.onChange}
                            disabled={(date) => date < new Date("1900-01-01")}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={editForm.control}
                  name="duration_minutes"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Duration (Minutes)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min="0"
                          placeholder="0"
                          {...field}
                          onChange={(e) => {
                            const value = e.target.value === "" ? undefined : parseInt(e.target.value);
                            field.onChange(value);
                          }}
                          value={field.value ?? ""}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={editForm.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Details (Optional)</FormLabel>
                    <FormControl>
                      <Textarea placeholder="Detailed notes about the activity" rows={3} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={editForm.control}
                name="response_status"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Response Status (Optional)</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger className="bg-background">
                          <SelectValue placeholder="Select response status" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent className="bg-background z-50">
                        {responseStatusOptions.map((status) => (
                          <SelectItem key={status.value} value={status.value}>
                            {status.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button type="submit" className="w-full" disabled={updateActivityMutation.isPending}>
                {updateActivityMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Update Activity
              </Button>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Summary Cards */}
      <ActivitiesSummaryCards
        todayCount={todayActivities.length}
        todayDonePercentage={todayDonePercentage}
        thisWeekCount={thisWeekActivities.length}
        lastWeekCount={lastWeekActivities.length}
        timeSpentToday={timeSpentToday}
        timeSpentYesterday={timeSpentYesterday}
        timeSpentThisWeek={timeSpentThisWeek}
        timeSpentLastWeek={timeSpentLastWeek}
      />

      {/* Filters */}
      <div className="flex flex-wrap gap-4 mb-6">
        <Select value={selectedTimeRange} onValueChange={setSelectedTimeRange}>
          <SelectTrigger className="w-[140px]">
            <SelectValue placeholder="Time" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="Today">Today</SelectItem>
            <SelectItem value="This week">This week</SelectItem>
            <SelectItem value="This month">This month</SelectItem>
            <SelectItem value="All">All</SelectItem>
          </SelectContent>
        </Select>

        <Select value={selectedUser} onValueChange={setSelectedUser}>
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder="My activities" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="my_activities">My activities</SelectItem>
            <SelectItem value="all">All users</SelectItem>
          </SelectContent>
        </Select>

        {/* Proposal Filter */}
        <Select value={selectedProposal} onValueChange={setSelectedProposal}>
          <SelectTrigger className="w-[220px]">
            <SelectValue placeholder="Related Proposal (Optional)" />
          </SelectTrigger>
          <SelectContent className="bg-background z-50">
            <SelectItem value="all">All Proposals</SelectItem>
            <SelectItem value="none">No Proposal</SelectItem>
            {proposals?.map((proposal) => (
              <SelectItem key={proposal.id} value={proposal.id}>
                {proposal.title}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Initiative Filter with Search */}
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className="w-[200px] justify-between"
            >
              {selectedInitiative === "all" 
                ? "All Initiatives" 
                : selectedInitiative === "none" 
                  ? "No Initiative"
                  : uniqueInitiatives.find(i => i.id === selectedInitiative)?.name || "Select Initiative"
              }
              <Search className="ml-2 h-4 w-4 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[200px] p-0 bg-background z-50" align="start">
            <div className="p-2">
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search initiatives..."
                  value={initiativeSearchTerm}
                  onChange={(e) => setInitiativeSearchTerm(e.target.value)}
                  className="pl-8"
                />
              </div>
            </div>
            <div className="max-h-[200px] overflow-y-auto">
              <div
                className={cn(
                  "flex items-center px-2 py-1.5 text-sm cursor-pointer hover:bg-accent",
                  selectedInitiative === "all" && "bg-accent"
                )}
                onClick={() => {
                  setSelectedInitiative("all");
                  setInitiativeSearchTerm("");
                }}
              >
                All Initiatives
              </div>
              <div
                className={cn(
                  "flex items-center px-2 py-1.5 text-sm cursor-pointer hover:bg-accent",
                  selectedInitiative === "none" && "bg-accent"
                )}
                onClick={() => {
                  setSelectedInitiative("none");
                  setInitiativeSearchTerm("");
                }}
              >
                No Initiative
              </div>
              {filteredInitiatives.map((initiative) => (
                <div
                  key={initiative.id}
                  className={cn(
                    "flex items-center px-2 py-1.5 text-sm cursor-pointer hover:bg-accent",
                    selectedInitiative === initiative.id && "bg-accent"
                  )}
                  onClick={() => {
                    setSelectedInitiative(initiative.id);
                    setInitiativeSearchTerm("");
                  }}
                >
                  {initiative.name}
                </div>
              ))}
              {filteredInitiatives.length === 0 && initiativeSearchTerm && (
                <div className="px-2 py-1.5 text-sm text-muted-foreground">
                  No initiatives found
                </div>
              )}
            </div>
          </PopoverContent>
        </Popover>

        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input
            placeholder="Search activities, clients, types..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Activities List */}
      <div className="space-y-4">
        {activitiesLoading && (
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <Card key={i}>
                <CardContent className="p-6">
                  <div className="flex items-start space-x-4">
                    <Skeleton className="h-10 w-10 rounded-full" />
                    <div className="flex-1 space-y-2">
                      <Skeleton className="h-4 w-3/4" />
                      <Skeleton className="h-4 w-1/2" />
                      <Skeleton className="h-4 w-full" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {!activitiesLoading && (
          Object.entries(groupedActivities).map(([groupKey, group]: [string, any]) => {
            // Get the primary activity (first one) for display purposes
            const primaryActivity = group.activities[0];
            const activityType = activityTypes.find(type => type.value === primaryActivity.type);
            const StatusIcon = activityType?.icon || FileText;
            
            // Get all unique people from all activities in the group
            const allPeople = group.activities.reduce((people, activity) => {
              if (activity.people && activity.people.length > 0) {
                activity.people.forEach(person => {
                  if (!people.find(p => p.id === person.id)) {
                    people.push(person);
                  }
                });
              }
              return people;
            }, [] as any[]);

            return (
              <Card key={groupKey} className="hover:shadow-md transition-shadow">
                <CardContent className="p-0">
                  <div className="p-6">
                     <div className="flex items-start justify-between">
                       <div className="flex items-start space-x-4 flex-1">
                          <div className={cn("p-2 rounded-full", activityType?.bgColor || "bg-gray-50")}>
                            <StatusIcon className={cn("h-5 w-5", activityType?.iconColor || "text-gray-600")} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-2">
                              <h3 className="font-semibold text-foreground">
                                {group.initiative?.name || primaryActivity.subject || "Activity"}
                              </h3>
                            </div>
                            
                            <div className="flex items-center gap-4 text-sm text-muted-foreground">
                              {group.client && (
                                <div className="flex items-center gap-1">
                                  <Building className="h-3 w-3" />
                                  <span>{group.client.name}</span>
                                </div>
                              )}
                              
                              {allPeople.length > 0 && (
                                <div className="flex items-center gap-1">
                                  <User className="h-3 w-3" />
                                  <span>{allPeople.map(p => `${p.first_name} ${p.last_name}`).join(', ')}</span>
                                </div>
                              )}
                            </div>
                            
                          </div>
                        </div>
                        
                         {/* Deployment date - moved to header row */}
                         <div className="flex flex-col items-end gap-2">
                           <div className="text-xs text-muted-foreground">
                             {getRelativeTime(primaryActivity.activity_date)}
                           </div>
                           {/* User info with avatar - show from primary activity */}
                           {(primaryActivity.assignee || primaryActivity.creator) && (
                             <div className="flex items-center gap-2 text-xs text-muted-foreground">
                               {(primaryActivity.assignee?.profile_image_url || primaryActivity.creator?.profile_image_url) && (
                                 <img 
                                   src={primaryActivity.assignee?.profile_image_url || primaryActivity.creator?.profile_image_url} 
                                   alt="User avatar"
                                   className="w-6 h-6 rounded-full object-cover"
                                 />
                               )}
                               <div className="flex flex-col">
                                 <div className="font-medium">
                                   {primaryActivity.assignee ? 
                                     (primaryActivity.assignee.full_name || `${primaryActivity.assignee.first_name || ''} ${primaryActivity.assignee.last_name || ''}`.trim() || 'Unknown User') :
                                     (primaryActivity.creator?.full_name || `${primaryActivity.creator?.first_name || ''} ${primaryActivity.creator?.last_name || ''}`.trim() || 'Unknown User')
                                   }
                                 </div>
                                 {/* Show "by manager" flag if created by someone else for this user */}
                                 {primaryActivity.assignee && primaryActivity.creator && 
                                  primaryActivity.creator.id !== primaryActivity.assignee.id && (
                                   <div className="text-xs text-orange-600 font-medium">
                                     by manager
                                   </div>
                                  )}
                                </div>
                             </div>
                           )}
                         </div>
                      </div>
                     
                     <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
                         {/* Status controls row - show summary for group */}
                         <div className="flex items-center gap-4">
                           <div className="flex items-center gap-2">
                             <Label htmlFor={`done-switch-${primaryActivity.id}`} className="text-sm text-muted-foreground">
                               Done
                             </Label>
                             <Switch
                               id={`done-switch-${primaryActivity.id}`}
                               checked={primaryActivity.status === 'Done'}
                               onCheckedChange={(checked) => {
                                 updateActivityStatusMutation.mutate({
                                   activityId: primaryActivity.id,
                                   status: checked ? 'Done' : 'Pending'
                                 });
                               }}
                             />
                           </div>
                          
                          {/* Show total duration for research activities */}
                          {group.activities.some(a => a.type === 'research') && (
                            <div className="flex items-center gap-2">
                              <Clock className="h-4 w-4 text-muted-foreground" />
                              <Badge variant="secondary" className="text-xs bg-blue-50 text-blue-700">
                                {group.activities.reduce((total, a) => total + (a.duration_minutes || 0), 0)} min total
                              </Badge>
                            </div>
                          )}

                          {/* Show response status if available */}
                          {primaryActivity.response_status && (
                            <div className="flex items-center gap-2">
                              <Popover>
                                <PopoverTrigger asChild>
                                  <button
                                    className={cn(
                                      "px-3 py-1 text-xs rounded-full border cursor-pointer hover:opacity-80 transition-opacity min-h-[32px] touch-manipulation",
                                      responseStatusOptions.find(s => s.value === primaryActivity.response_status)?.color || "bg-gray-100 text-gray-700"
                                    )}
                                  >
                                    {responseStatusOptions.find(s => s.value === primaryActivity.response_status)?.label || primaryActivity.response_status}
                                  </button>
                                </PopoverTrigger>
                                <PopoverContent className="w-auto p-1 bg-background z-50 border shadow-lg" align="start">
                                  <div className="flex flex-col gap-1">
                                    {responseStatusOptions.map((status) => (
                                      <button
                                        key={status.value}
                                        className={cn(
                                          "px-3 py-2 text-xs rounded-md text-left hover:bg-accent transition-colors min-h-[36px] touch-manipulation",
                                          primaryActivity.response_status === status.value && "bg-accent"
                                        )}
                                        onClick={() => {
                                          updateResponseStatusMutation.mutate({
                                            activityId: primaryActivity.id,
                                            responseStatus: status.value
                                          });
                                        }}
                                      >
                                        <span className={cn("inline-block px-2 py-1 rounded text-xs border", status.color)}>
                                          {status.label}
                                        </span>
                                      </button>
                                    ))}
                                  </div>
                                </PopoverContent>
                              </Popover>
                            </div>
                          )}

                          {/* Add response status if not set */}
                          {!primaryActivity.response_status && (
                            <div className="flex items-center gap-2">
                              <Popover>
                                <PopoverTrigger asChild>
                                  <button className="h-8 px-3 text-xs border border-dashed border-muted-foreground text-muted-foreground rounded-md hover:bg-accent transition-colors min-h-[32px] touch-manipulation">
                                    + Status
                                  </button>
                                </PopoverTrigger>
                                <PopoverContent className="w-auto p-1 bg-background z-50 border shadow-lg" align="start">
                                  <div className="flex flex-col gap-1">
                                    {responseStatusOptions.map((status) => (
                                      <button
                                        key={status.value}
                                        className="px-3 py-2 text-xs rounded-md text-left hover:bg-accent transition-colors min-h-[36px] touch-manipulation"
                                        onClick={() => {
                                          updateResponseStatusMutation.mutate({
                                            activityId: primaryActivity.id,
                                            responseStatus: status.value
                                          });
                                        }}
                                      >
                                        <span className={cn("inline-block px-2 py-1 rounded text-xs border", status.color)}>
                                          {status.label}
                                        </span>
                                      </button>
                                    ))}
                                  </div>
                                </PopoverContent>
                              </Popover>
                            </div>
                          )}
                        </div>

                        {/* Action buttons row */}
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          {/* Edit button for primary activity */}
                         <Button
                           variant="ghost"
                           size="sm"
                           className="h-6 w-6 p-0 hover:bg-accent"
                           onClick={() => {
                             setEditingActivity(primaryActivity);
                             if (primaryActivity.type === 'research') {
                               setIsResearchModalOpen(true);
                             } else {
                               setIsEditModalOpen(true);
                             }
                           }}
                         >
                           <Edit className="h-3 w-3" />
                         </Button>
                         
                         {/* Delete button for primary activity */}
                         <AlertDialog>
                           <AlertDialogTrigger asChild>
                             <Button
                               variant="ghost"
                               size="sm"
                               className="h-6 w-6 p-0 hover:bg-destructive hover:text-destructive-foreground"
                             >
                               <Trash2 className="h-3 w-3" />
                             </Button>
                           </AlertDialogTrigger>
                           <AlertDialogContent>
                             <AlertDialogHeader>
                               <AlertDialogTitle>Delete Activity</AlertDialogTitle>
                               <AlertDialogDescription>
                                 Are you sure you want to delete this activity from the group? This action cannot be undone.
                               </AlertDialogDescription>
                             </AlertDialogHeader>
                             <AlertDialogFooter>
                               <AlertDialogCancel>Cancel</AlertDialogCancel>
                               <AlertDialogAction
                                 className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                 onClick={() => deleteActivityMutation.mutate(primaryActivity.id)}
                               >
                                 Delete
                               </AlertDialogAction>
                             </AlertDialogFooter>
                           </AlertDialogContent>
                         </AlertDialog>
                       </div>
                     </div>
                   </div>
                 </CardContent>
               </Card>
             );
           })
         )}
      </div>

      {!activitiesLoading && Object.keys(groupedActivities).length === 0 && (
        <div className="text-center py-12">
          <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium mb-2">No activities found</h3>
          <p className="text-muted-foreground">
            Try adjusting your filters or search term, or log your first activity.
          </p>
        </div>
      )}

      {/* Research Activity Form */}
      <ResearchActivityForm
        open={isResearchModalOpen}
        onOpenChange={setIsResearchModalOpen}
        projects={projects || []}
        editingActivity={editingActivity?.type === 'research' ? editingActivity : null}
        onEditComplete={() => {
          setEditingActivity(null);
          setIsResearchModalOpen(false);
        }}
      />
    </div>
  );
}
